import { Box3, Intersection, Points, Ray, Raycaster, Vector3 } from 'three';

import { ProgressController } from '../../../react/utils/progress-controller';
import { PromiseAbortSignal } from '../../../react/utils/promise-abort-controller';
import { SkyviewCamera } from '../../cameras/types';
import { CoordinateManager } from '../../coordinate-manager';
import { Pick } from '../../utilities/pick';
import { Cesium3dTilesPointsMaterial } from './cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-points-material';
import { CesiumPointCloud } from './cesium-point-cloud/cesium-point-cloud';
import { CesiumPointsMaterial } from './cesium-point-cloud/points/material/cesium-point-material';
import { PointCloud, PointSizeInterpolationSettings } from './types';

export class PointCloudHandle extends PointCloud {
  private static _instance = new PointCloudHandle();
  private _activePointCloud?: PointCloud;

  private _displayPointCloud = false;

  static get instance() {
    return this._instance;
  }

  set displayPointCloud(state: boolean) {
    this._displayPointCloud = state;
    if (this.activePointCloud && this.activePointCloud instanceof CesiumPointCloud) {
      this.activePointCloud.visible = this._displayPointCloud;
    }
  }

  get pointSizeInterpolationSettings(): PointSizeInterpolationSettings {
    return {
      pointSizeInterpolationType: CesiumPointsMaterial.instance.attenuation ? 'Dynamic' : 'Fixed',
      size: CesiumPointsMaterial.instance.pointScale,
    };
  }

  set displayPointCloudClasses(state: boolean) {
    CesiumPointsMaterial.instance.displayClasses = state;
    Cesium3dTilesPointsMaterial.instance.displayClasses = state;
  }

  get activePointCloud() {
    return this._activePointCloud;
  }

  private constructor() {
    super();

    CesiumPointsMaterial.instance.attenuation = true;
    CesiumPointsMaterial.instance.pointScale = 1.0;
  }

  setActivePointCloud(pointCloud?: PointCloud) {
    this._activePointCloud = pointCloud;

    Pick.instance.setActivePointCloud(this.activePointCloud);
  }

  loadLodChunks(camera: SkyviewCamera): Promise<void> {
    if (!this.activePointCloud) {
      return Promise.resolve();
    }
    this.updatePointCloudPosition();

    return this.activePointCloud.loadLodChunks(camera);
  }

  cullLodChunks(camera: SkyviewCamera): void {
    if (!this.activePointCloud) {
      return;
    }

    this.updatePointCloudPosition();
    this.activePointCloud?.cullLodChunks(camera);
  }

  pick(ray: Raycaster): Intersection[] {
    this.updatePointCloudPosition();
    return this.activePointCloud?.pick(ray) ?? [];
  }

  intersect(rayCaster: Raycaster): Intersection[] {
    this.updatePointCloudPosition();
    return this.activePointCloud?.intersect(rayCaster) ?? [];
  }

  pickNearCursor(camera: SkyviewCamera, rayCaster: Raycaster): Intersection | undefined {
    this.updatePointCloudPosition();
    return this.activePointCloud?.pickNearCursor(camera, rayCaster);
  }

  async getMaxLodChunksFromRays(
    rays: Ray[],
    signal?: PromiseAbortSignal,
    onProgress?: ProgressController,
  ): Promise<Points[]> {
    return this.activePointCloud?.getMaxLodChunksFromRays(rays, signal, onProgress) ?? [];
  }

  async getMaxLodChunksWithinBoundingBox(
    bbox: Box3,
    signal?: PromiseAbortSignal,
    onProgress?: ProgressController,
  ): Promise<Points[]> {
    return this.activePointCloud?.getMaxLodChunksWithinBoundingBox(bbox, signal, onProgress) ?? [];
  }

  available(): boolean {
    return !!this.activePointCloud?.available();
  }

  getBoundingBox(): Box3 {
    return this.activePointCloud?.getBoundingBox() ?? new Box3();
  }

  getOrigo() {
    return this.activePointCloud?.getOrigo() ?? new Vector3();
  }

  getPosition() {
    return this.activePointCloud?.position ?? new Vector3();
  }

  /** Clears the point cloud and lets it go, so nothing picks from or shows it again. */
  clearCustom(): void {
    this.activePointCloud?.clearCustom();
    this.setActivePointCloud(undefined);
  }

  setPointSizeInterpolationSettings(
    pointSizeInterpolationSettings: PointSizeInterpolationSettings,
  ) {
    const isDynamic = pointSizeInterpolationSettings.pointSizeInterpolationType === 'Dynamic';
    CesiumPointsMaterial.instance.attenuation = isDynamic;
    CesiumPointsMaterial.instance.pointScale = pointSizeInterpolationSettings.size;
    Cesium3dTilesPointsMaterial.instance.attenuation = isDynamic;
    Cesium3dTilesPointsMaterial.instance.pointScale = pointSizeInterpolationSettings.size;
  }

  /**
   * Places the point cloud where its origo is in the scene, the one offset it is drawn, loaded and
   * picked with, so the scene's origo can be anywhere.
   */
  private updatePointCloudPosition() {
    const projectionOrigo = CoordinateManager.getInstance().getProjectionOrigo();
    if (!this.activePointCloud || !projectionOrigo) {
      return;
    }

    this.activePointCloud.position.copy(this.getOrigo()).sub(projectionOrigo);
    this.activePointCloud.updateWorldMatrix(false, true);
  }
}
